import type {
  DashboardAccessTokenItem,
  DashboardIssueItem,
  DashboardSlice,
  DashboardSubmissionItem,
  DashboardWorkQueue,
  DashboardWorkQueueItem,
} from './api-client';
import type { PageUser } from './page-runtime';

export const EMAIL_VERIFICATION_SETTINGS_HREF = '/settings?focus=email-verification&next=%2Fdashboard%3Fsection%3Daccess-tokens';

export interface DashboardNextStepInput {
  issues: DashboardSlice<DashboardIssueItem>;
  submissions: DashboardSlice<DashboardSubmissionItem>;
  skills: DashboardSlice<unknown>;
  access_tokens: DashboardSlice<DashboardAccessTokenItem>;
  work_queue?: DashboardWorkQueue;
}

export type DashboardNextStepAccount = Pick<PageUser, 'username' | 'role' | 'email_verified_at' | 'email_verification_required'> | null | undefined;
export type DashboardNextStepRole = 'anonymous' | 'account_setup' | 'developer_setup' | 'developer' | 'requester' | 'maintainer' | 'skill_owner';
export type DashboardTaskTone = 'amber' | 'emerald' | 'blue';

export interface DashboardNextStep {
  role: DashboardNextStepRole;
  roleLabel: string;
  title: string;
  body: string;
  href: string;
  action: string;
  icon: string;
  tone: DashboardTaskTone;
  queueKey?: keyof DashboardWorkQueue;
}

export interface DashboardWorkQueueCard {
  key: string;
  role: DashboardNextStepRole;
  roleLabel: string;
  title: string;
  body: string;
  countLabel: string;
  href: string;
  action: string;
  icon: string;
  tone: DashboardTaskTone;
}

interface DashboardWorkCandidate {
  card: DashboardWorkQueueCard;
  nextStep: DashboardNextStep;
  queueOrder: number;
  nextOrder: number;
}

const EMPTY_WORK_QUEUE: DashboardWorkQueue = {
  requester_review: { items: [], total: 0 },
  maintainer_review: { items: [], total: 0 },
  maintainer_publish: { items: [], total: 0 },
  owner_approval: { items: [], total: 0 },
  certification_review: { items: [], total: 0 },
};

function candidate(
  card: DashboardWorkQueueCard,
  queueOrder: number,
  nextOrder: number,
  nextStep: Partial<DashboardNextStep> = {},
): DashboardWorkCandidate {
  return {
    card,
    queueOrder,
    nextOrder,
    nextStep: {
      role: card.role,
      roleLabel: card.roleLabel,
      title: card.title,
      body: card.body,
      href: card.href,
      action: card.action,
      icon: card.icon,
      tone: card.tone,
      ...nextStep,
    },
  };
}

function queueCount(total: number, singular: string, plural = singular + 's'): string {
  return total === 1 ? `1 ${singular}` : `${total} ${plural}`;
}

function firstQueueItem(queue: DashboardSlice<DashboardWorkQueueItem>): DashboardWorkQueueItem | null {
  return queue.items[0] ?? null;
}

function queueIssueLabel(item: DashboardWorkQueueItem | null): string {
  return item?.issue_title || (item ? `Issue #${item.issue_id}` : 'The oldest waiting item');
}

function queueSkillLabel(item: DashboardWorkQueueItem | null): string {
  if (!item) return 'a published skill';
  return item.target_skill_name || (item.target_skill_id ? `skill #${item.target_skill_id}` : 'a published skill');
}

function requesterQueueCopy(item: DashboardWorkQueueItem | null): {
  title: string;
  action: string;
  body: string;
} {
  const issue = queueIssueLabel(item);
  const selfDisclosure = item?.is_self_implementation
    ? ' Requester and implementer share one account; this is not independent certification.'
    : '';
  if (item?.requester_next_action === 'publish_accepted_implementation') {
    return {
      title: 'Publish accepted implementation',
      action: 'Open publication step',
      body: `${issue} has your recorded acceptance and is ready for a separate Community Publish decision.${selfDisclosure}`,
    };
  }
  if (item?.requester_next_action === 'review_updated_implementation') {
    return {
      title: 'Review updated implementation',
      action: 'Review updated submission',
      body: `${issue} has a new revision after an earlier Requester Review.${selfDisclosure}`,
    };
  }
  return {
    title: 'Review implementation',
    action: 'Review submission',
    body: `${issue} is waiting for the Issue Creator's decision.${selfDisclosure}`,
  };
}

function activeDeveloperSubmissions(data: DashboardNextStepInput): DashboardSubmissionItem[] {
  const priority = new Map([['needs_work', 0], ['approved', 1], ['open', 2], ['submitted', 3]]);
  const representedSubmissionIds = new Set([
    ...(data.work_queue?.requester_review.items ?? []),
    ...(data.work_queue?.owner_approval.items ?? []),
  ].filter((item) => item.is_self_implementation).map((item) => item.submission_id));
  return [...data.submissions.items]
    .filter((submission) => priority.has(submission.status) && !representedSubmissionIds.has(submission.id))
    .sort((a, b) => (priority.get(a.status) ?? 99) - (priority.get(b.status) ?? 99)
      || String(a.created_at || '').localeCompare(String(b.created_at || '')));
}

function setupCandidate(data: DashboardNextStepInput, account: NonNullable<DashboardNextStepAccount>): DashboardWorkCandidate | null {
  if (account.email_verification_required === true && !account.email_verified_at) {
    return candidate({
      key: 'account_email_verification', role: 'account_setup', roleLabel: 'Account setup',
      title: 'Verify email', body: 'Account verification is blocking agent token creation.', countLabel: 'Blocked',
      href: EMAIL_VERIFICATION_SETTINGS_HREF, action: 'Verify email', icon: 'fa-envelope-circle-check', tone: 'amber',
    }, 0, 50, {
      title: 'Verify your email',
      body: 'Verify your account email before creating agent tokens. This also keeps password recovery available.',
    });
  }
  if (!data.access_tokens.items.some((token) => token.status !== 'revoked')) {
    return candidate({
      key: 'developer_token_setup', role: 'developer_setup', roleLabel: 'Developer setup',
      title: 'Connect an MCP client', body: 'Create an access token before assigning agent work to this account.', countLabel: 'Setup',
      href: '#access-tokens', action: 'Set up token', icon: 'fa-plug', tone: 'emerald',
    }, 0, 50, {
      body: 'Create one access token, then use the same SkillFab user account from your AI client.',
    });
  }
  return null;
}

function roleQueueCandidates(data: DashboardNextStepInput, account: NonNullable<DashboardNextStepAccount>): DashboardWorkCandidate[] {
  const queue = data.work_queue ?? EMPTY_WORK_QUEUE;
  const cards: DashboardWorkCandidate[] = [];
  const maintainerReview = firstQueueItem(queue.maintainer_review);
  const certificationReview = queue.certification_review?.items[0] ?? null;
  const maintainerPublish = firstQueueItem(queue.maintainer_publish);
  const ownerApproval = firstQueueItem(queue.owner_approval);
  const requesterReview = firstQueueItem(queue.requester_review);
  const requesterCopy = requesterQueueCopy(requesterReview);

  if (account.role === 'maintainer' && (queue.certification_review?.total ?? 0) > 0) cards.push(candidate({
    key: 'certification_review', role: 'maintainer', roleLabel: 'Maintainer', title: 'Review certification request',
    body: `${certificationReview?.skill_name || 'A community skill'} v${certificationReview?.version || ''} is pinned for static review.`,
    countLabel: queueCount(queue.certification_review?.total ?? 0, 'certification request'),
    href: certificationReview ? `/review?certification_request=${certificationReview.request_id}` : '/review',
    action: 'Open certification review', icon: 'fa-shield-halved', tone: 'amber',
  }, 5, 5, {
    roleLabel: 'Certification queue',
    queueKey: 'certification_review',
  }));

  if (account.role === 'maintainer' && queue.maintainer_review.total > 0) cards.push(candidate({
    key: 'maintainer_review', role: 'maintainer', roleLabel: 'Maintainer', title: 'Review submitted skills',
    body: `Start with ${queueIssueLabel(maintainerReview)}.`, countLabel: queueCount(queue.maintainer_review.total, 'submission'),
    href: '/review', action: 'Open review queue', icon: 'fa-clipboard-check', tone: 'amber',
  }, 10, 10, {
    roleLabel: 'Maintainer queue',
    body: `${queueCount(queue.maintainer_review.total, 'submission')} need static review. Start with ${queueIssueLabel(maintainerReview)}.`,
    queueKey: 'maintainer_review',
  }));

  if (account.role === 'maintainer' && queue.maintainer_publish.total > 0) cards.push(candidate({
    key: 'maintainer_publish', role: 'maintainer', roleLabel: 'Maintainer', title: 'Publish approved submissions',
    body: `Start with ${queueIssueLabel(maintainerPublish)}.`, countLabel: queueCount(queue.maintainer_publish.total, 'approved submission'),
    href: maintainerPublish ? `/submissions/${maintainerPublish.submission_id}` : '/review',
    action: 'Open publish gate', icon: 'fa-rocket', tone: 'amber',
  }, 20, 20, {
    roleLabel: 'Publication gates',
    body: `${queueCount(queue.maintainer_publish.total, 'approved submission')} are waiting on publication or certification. Start with ${queueIssueLabel(maintainerPublish)}.`,
    queueKey: 'maintainer_publish',
  }));

  if (queue.owner_approval.total > 0) cards.push(candidate({
    key: 'owner_approval', role: 'skill_owner', roleLabel: 'Skill owner',
    title: ownerApproval?.is_self_implementation ? 'Publish your skill update' : 'Review skill update request',
    body: ownerApproval?.is_self_implementation
      ? `${queueSkillLabel(ownerApproval)} has your implementation waiting for owner approval.`
      : `${queueSkillLabel(ownerApproval)} has an update request waiting for owner attention.`,
    countLabel: queueCount(queue.owner_approval.total, 'update request'),
    href: ownerApproval ? `/submissions/${ownerApproval.submission_id}` : '/dashboard?section=skills',
    action: 'Review update', icon: 'fa-user-shield', tone: 'amber',
  }, 30, 30, {
    roleLabel: 'Skill owner queue',
    body: `${queueCount(queue.owner_approval.total, 'version update request')} need owner attention for ${queueSkillLabel(ownerApproval)}.`,
    queueKey: 'owner_approval',
  }));

  if (queue.requester_review.total > 0) cards.push(candidate({
    key: 'requester_review', role: 'requester', roleLabel: 'Requester',
    title: requesterCopy.title,
    body: requesterCopy.body,
    countLabel: queueCount(queue.requester_review.total, 'submission'),
    href: requesterReview ? `/submissions/${requesterReview.submission_id}` : '/dashboard?section=issues',
    action: requesterCopy.action, icon: 'fa-inbox', tone: 'amber',
  }, 40, 40, {
    roleLabel: 'Requester queue',
    title: requesterCopy.title,
    body: `${requesterCopy.body} ${queueCount(queue.requester_review.total, 'submission')} currently ${queue.requester_review.total === 1 ? 'needs' : 'need'} requester attention.`,
    action: requesterCopy.action,
    queueKey: 'requester_review',
  }));
  return cards;
}

function developerCandidate(data: DashboardNextStepInput): DashboardWorkCandidate | null {
  const active = activeDeveloperSubmissions(data);
  const submission = active[0];
  if (!submission) return null;
  const issue = submission.issue_title || `Issue #${submission.issue_id}`;
  const shared = { countLabel: queueCount(active.length, 'active submission'), href: `/submissions/${submission.id}`, action: 'Open submission' };

  if (submission.status === 'needs_work') return candidate({
    ...shared, key: 'developer_needs_work', role: 'developer', roleLabel: 'Developer', title: 'Revise reviewed work',
    body: `${issue} needs changes before it can move forward.`, icon: 'fa-comments', tone: 'amber',
  }, 50, 45, { roleLabel: 'Developer queue', title: 'Respond to review feedback', body: `${issue} needs changes before it can be merged / published.` });
  if (submission.status === 'approved') return candidate({
    ...shared, key: 'developer_approved', role: 'developer', roleLabel: 'Developer', title: 'Publish approved work',
    body: `${issue} is approved and waiting for publication follow-through.`, icon: 'fa-rocket', tone: 'amber',
  }, 50, 45, { roleLabel: 'Developer queue', body: `${issue} is approved. Publish or confirm the remaining publication authority from the submission page.` });
  if (submission.status === 'submitted') return candidate({
    ...shared, key: 'developer_submitted', role: 'developer', roleLabel: 'Developer', title: 'Watch submitted work',
    body: `${issue} is waiting for review.`, icon: 'fa-hourglass-half', tone: 'blue',
  }, 50, 45, { roleLabel: 'Developer queue', body: `${issue} is waiting for static review. Open it to inspect review history or diagnostics.` });
  return candidate({
    ...shared, key: 'developer_open', role: 'developer', roleLabel: 'Developer', title: 'Finish active work',
    body: `${issue} is still open. Push code and request review when ready.`, icon: 'fa-code-branch', tone: 'blue',
  }, 50, 45, { roleLabel: 'Developer queue', title: 'Finish your active submission' });
}

function issueCandidate(data: DashboardNextStepInput): DashboardWorkCandidate | null {
  const representedIssueIds = new Set([
    ...(data.work_queue?.requester_review.items ?? []),
    ...(data.work_queue?.owner_approval.items ?? []),
  ].map((item) => item.issue_id));
  const issue = data.issues.items.find((item) => (
    item.status !== 'completed' && !representedIssueIds.has(item.id)
  ));
  return issue ? candidate({
    key: 'requester_open_issue', role: 'requester', roleLabel: 'Requester', title: 'Track open issue',
    body: `${issue.title} is still active.`, countLabel: 'Open issue', href: `/issues/${issue.id}`,
    action: 'View issue', icon: 'fa-lightbulb', tone: 'blue',
  }, 60, 70, {
    roleLabel: 'Requester queue', title: 'Track your open issue',
    body: `${issue.title} is still active. Check status, submissions, and review history.`,
  }) : null;
}

function browseCandidate(): DashboardWorkCandidate {
  return candidate({
    key: 'browse_issues', role: 'developer', roleLabel: 'Developer', title: 'Pick an issue to implement',
    body: 'Your queue is clear.', countLabel: 'No waiting work', href: '/issues', action: 'Browse issues',
    icon: 'fa-list-check', tone: 'emerald',
  }, 80, 80, {
    roleLabel: 'Developer queue',
    body: 'Your account is ready. Browse open issues and start with the MCP workflow state tool.',
  });
}

function anonymousCandidate(): DashboardWorkCandidate {
  return candidate({
    key: 'anonymous_sign_in', role: 'anonymous', roleLabel: 'Visitor', title: 'Sign in to see your queue',
    body: 'Issues, submissions, reviews, and skill owner requests appear after sign-in.', countLabel: 'Sign-in required',
    href: '/login?redirect=%2Fdashboard', action: 'Sign in', icon: 'fa-right-to-bracket', tone: 'blue',
  }, 0, 0, {
    title: 'Sign in to continue',
    body: 'Sign in to see your issues, submissions, skill ownership requests, and review work.',
  });
}

function workCandidates(data: DashboardNextStepInput, account: NonNullable<DashboardNextStepAccount>): DashboardWorkCandidate[] {
  const candidates = roleQueueCandidates(data, account);
  const setup = setupCandidate(data, account);
  const developer = developerCandidate(data);
  const issue = issueCandidate(data);
  if (setup) candidates.push(setup);
  if (developer) candidates.push(developer);
  if (issue) candidates.push(issue);
  return candidates.length ? candidates : [browseCandidate()];
}

export function selectDashboardWorkQueue(data: DashboardNextStepInput, account?: DashboardNextStepAccount): DashboardWorkQueueCard[] {
  const candidates = account ? workCandidates(data, account) : [anonymousCandidate()];
  return candidates.sort((a, b) => a.queueOrder - b.queueOrder).slice(0, 6).map(({ card }) => card);
}

export function selectDashboardNextStep(data: DashboardNextStepInput, account?: DashboardNextStepAccount): DashboardNextStep {
  const candidates = account ? workCandidates(data, account) : [anonymousCandidate()];
  return candidates.sort((a, b) => a.nextOrder - b.nextOrder)[0].nextStep;
}
